import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  LockReset as LockResetIcon,
  CheckCircle as CheckCircleIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useWhitelabel } from '../context/WhitelabelContext';
import api, { handleCatch } from '../api';

const ForgotPasswordScreen = () => {
  const [data, setData] = useState({
    mobileNumber: '',
    isValidMobileNumber: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { config: whitelabelConfig } = useWhitelabel();
  const navigate = useNavigate();

  const handleMobileNumberChange = (val) => {
    if (val.trim().length === 10) {
      setData({
        ...data,
        mobileNumber: val,
        isValidMobileNumber: true,
      });
    } else {
      setData({
        ...data,
        mobileNumber: val,
        isValidMobileNumber: false,
      });
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (data.mobileNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    const header = {
      mobile: data.mobileNumber,
    };

    setLoading(true);
    try {
      const response = await api.post('api/TOKEN/forget-password-client-otp', header);
      navigate('/verify-otp', {
        state: {
          mobile: data.mobileNumber,
          secretKey: response.data,
          isPasswordReset: true,
        },
      });
    } catch (error) {
      const errorMsg = handleCatch(error);
      setError(errorMsg || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get colors from whitelabel config
  const primaryColor = whitelabelConfig?.primaryColor || '#0066CC';
  const secondaryColor = whitelabelConfig?.secondaryColor || '#004C99';

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: primaryColor }}>

      {/* ── Top gradient banner ── */}
      <Box
        sx={{
          background: `linear-gradient(160deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          px: 2,
          pt: 3,
          pb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={() => navigate('/login')}
          sx={{
            position: 'absolute',
            left: 12,
            top: 12,
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.15)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' },
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1,
            mb: 1.5,
          }}
        >
          <LockResetIcon sx={{ fontSize: 36, color: '#fff' }} />
        </Box>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 22, textAlign: 'center' }}>
          Forgot Password?
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, mt: 0.5, textAlign: 'center', px: 3, lineHeight: 1.5 }}>
          Enter your mobile and we'll send an OTP to reset your password.
        </Typography>
      </Box>

      {/* ── White form card (scrollable) ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#fff',
          borderRadius: '20px 20px 0 0',
          px: 2.5,
          pt: 3,
          pb: 4,
          mt: '-20px',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography sx={{ mb: 0.75, fontWeight: 600, fontSize: 13, color: '#333' }}>
            Mobile Number
          </Typography>
          <TextField
            fullWidth
            autoFocus
            placeholder="Enter your 10-digit mobile number"
            value={data.mobileNumber}
            onChange={(e) => handleMobileNumberChange(e.target.value)}
            error={!data.isValidMobileNumber && data.mobileNumber.length > 0}
            helperText={
              !data.isValidMobileNumber && data.mobileNumber.length > 0
                ? 'Mobile number must be 10 digits long.'
                : ''
            }
            inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: primaryColor }} />
                </InputAdornment>
              ),
              endAdornment: data.isValidMobileNumber && (
                <InputAdornment position="end">
                  <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#f8f9fa',
                '& fieldset': { borderColor: '#e9ecef' },
                '&:hover fieldset': { borderColor: primaryColor },
                '&.Mui-focused fieldset': { borderColor: primaryColor },
                '&.Mui-error': { backgroundColor: '#FFF5F5' },
              },
            }}
          />

          {/* Send OTP button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !data.isValidMobileNumber}
            startIcon={loading ? null : <SendIcon />}
            sx={{
              height: 50,
              borderRadius: 25,
              fontSize: 16,
              fontWeight: 700,
              textTransform: 'none',
              backgroundColor: data.isValidMobileNumber ? primaryColor : '#ccc',
              boxShadow: data.isValidMobileNumber ? `0 4px 14px ${primaryColor}55` : 'none',
              '&:hover': { backgroundColor: data.isValidMobileNumber ? secondaryColor : '#ccc' },
              '&:disabled': { backgroundColor: '#ccc', color: '#fff' },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Send OTP'}
          </Button>

          {/* Back to Sign In */}
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/login')}
            disabled={loading}
            startIcon={<LoginIcon />}
            sx={{
              mt: 1.5,
              height: 50,
              borderRadius: 25,
              fontSize: 15,
              fontWeight: 600,
              textTransform: 'none',
              borderWidth: 2,
              borderColor: primaryColor,
              color: primaryColor,
              '&:hover': { borderWidth: 2, borderColor: secondaryColor, backgroundColor: `${primaryColor}0A` },
            }}
          >
            Back to Sign In
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordScreen;
